/**
 * Lead Command Center - Main Page Component
 * The central hub for managing all contacts/leads
 * Now with real-time WebSocket updates and Red Alert notifications
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import {
  Users, Phone, Mail, MapPin, Calendar, Clock, DollarSign,
  Plus, Search, ChevronRight, ChevronDown, MoreVertical,
  Trophy, Archive, Snowflake, AlertTriangle, CheckCircle2,
  Zap, TrendingUp, Target, Filter, X, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Sub-components
import StatsStrip from "../components/leads/StatsStrip";
import ContactTable from "../components/leads/ContactTable";
import ContactDetail from "../components/leads/ContactDetail";
import CreateContactModal from "../components/leads/CreateContactModal";
import LostReasonModal from "../components/leads/LostReasonModal";
import IceboxModal from "../components/leads/IceboxModal";
import RedAlertModal from "../components/leads/RedAlertModal";
import ConnectionStatus from "../components/leads/ConnectionStatus";
import SiteVisitScheduler from "../components/leads/SiteVisitScheduler";

// WebSocket hook
import useWebSocket from "../hooks/useWebSocket";

// Pipeline stage configuration
const PIPELINE_STAGES = [
  { key: "new_contact", label: "New Contact", color: "bg-blue-500", textColor: "text-blue-400" },
  { key: "site_visit", label: "Site Visit", color: "bg-purple-500", textColor: "text-purple-400" },
  { key: "estimate_sent", label: "Estimate Sent", color: "bg-orange-500", textColor: "text-orange-400" },
  { key: "follow_up", label: "Follow Up", color: "bg-pink-500", textColor: "text-pink-400" },
];

const ARCHIVE_TABS = [
  { key: "won", label: "Won", color: "bg-emerald-500", textColor: "text-emerald-400", icon: Trophy },
  { key: "lost", label: "Lost", color: "bg-red-500", textColor: "text-red-400", icon: Archive },
  { key: "icebox", label: "Icebox", color: "bg-cyan-500", textColor: "text-cyan-400", icon: Snowflake },
];

const LeadCommandCenter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Check if user is owner/admin (can delete contacts)
  const canDelete = user?.role === 'owner' || user?.role === 'admin';
  
  // Data state
  const [contacts, setContacts] = useState([]);
  const [counts, setCounts] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // UI state
  const [activeTab, setActiveTab] = useState("pipeline"); // 'pipeline' or 'archive'
  const [activeStage, setActiveStage] = useState(null); // null = all, or stage key
  const [archiveFilter, setArchiveFilter] = useState("won"); // 'won', 'lost', 'icebox'
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal state
  const [selectedContact, setSelectedContact] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [showIceboxModal, setShowIceboxModal] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactForAction, setContactForAction] = useState(null);
  const [newlyCreatedContact, setNewlyCreatedContact] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Red Alert state
  const [redAlert, setRedAlert] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // WebSocket real-time connection
  const { isConnected, connectionError, connect: reconnectWs } = useWebSocket({
    userId: 'lead-command-center',
    onRedAlert: (payload) => {
      console.log('[LeadCommandCenter] Red Alert received:', payload);
      setRedAlert(payload);
      // Also refresh contacts to show the new lead
      loadContacts();
    },
    onContactCreated: (payload) => {
      console.log('[LeadCommandCenter] Contact created:', payload);
      loadContacts();
      loadStats();
    },
    onContactUpdated: (payload) => {
      console.log('[LeadCommandCenter] Contact updated:', payload);
      // Update in local state if the contact is in view
      setContacts(prev => prev.map(c => 
        c.contact_id === payload.contact_id ? { ...c, ...payload.contact } : c
      ));
      // Update selected contact if open
      if (selectedContact?.contact_id === payload.contact_id) {
        setSelectedContact(prev => ({ ...prev, ...payload.contact }));
      }
    },
    onContactStageChanged: (payload) => {
      console.log('[LeadCommandCenter] Stage changed:', payload);
      loadContacts();
      loadStats();
    },
    onContactStatusChanged: (payload) => {
      console.log('[LeadCommandCenter] Status changed:', payload);
      loadContacts();
      loadStats();
    },
    onContactDeleted: (payload) => {
      console.log('[LeadCommandCenter] Contact deleted:', payload);
      setContacts(prev => prev.filter(c => c.contact_id !== payload.contact_id));
      if (selectedContact?.contact_id === payload.contact_id) {
        setSelectedContact(null);
      }
      loadStats();
    },
    onStatsUpdated: (payload) => {
      console.log('[LeadCommandCenter] Stats updated:', payload);
      setStats(prev => ({ ...prev, ...payload }));
    },
  });

  // Load contacts
  const loadContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      
      if (activeTab === "pipeline") {
        params.set("status", "active");
        if (activeStage) {
          params.set("stage", activeStage);
        }
      } else {
        params.set("status", archiveFilter);
      }
      
      if (searchQuery) {
        params.set("search", searchQuery);
      }
      
      params.set("page", page.toString());
      params.set("limit", "50");
      
      const response = await API.get(`/contacts?${params.toString()}`);
      setContacts(response.data.contacts || []);
      setCounts(response.data.counts || {});
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error("Failed to load contacts:", error);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeStage, archiveFilter, searchQuery, page]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await API.get("/contacts/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadContacts();
    loadStats();
  }, [loadContacts, loadStats]);

  // Handle URL params for deep linking
  useEffect(() => {
    const contactId = searchParams.get("contact");
    if (contactId && contacts.length > 0) {
      const contact = contacts.find(c => c.contact_id === contactId);
      if (contact) {
        setSelectedContact(contact);
      }
    }
  }, [searchParams, contacts]);

  // Refresh data
  const refreshData = () => {
    loadContacts();
    loadStats();
  };

  // Stage change handler
  const handleStageChange = async (contactId, newStage) => {
    try {
      await API.patch(`/contacts/${contactId}/stage`, { stage: newStage });
      toast.success(`Moved to ${PIPELINE_STAGES.find(s => s.key === newStage)?.label}`);
      refreshData();
      
      // Update selected contact if open
      if (selectedContact?.contact_id === contactId) {
        const response = await API.get(`/contacts/${contactId}`);
        setSelectedContact(response.data.contact);
      }
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  // Mark as won
  const handleMarkWon = async (contactId, finalValue, notes) => {
    try {
      await API.post(`/contacts/${contactId}/won`, { final_value: finalValue, notes });
      toast.success("🎉 Contact marked as won!");
      refreshData();
      setSelectedContact(null);
    } catch (error) {
      toast.error("Failed to mark as won");
    }
  };

  // Mark as lost
  const handleMarkLost = async (reason, notes) => {
    if (!contactForAction) return;
    try {
      await API.post(`/contacts/${contactForAction.contact_id}/lost`, { reason, notes });
      toast.success("Contact marked as lost");
      refreshData();
      setShowLostModal(false);
      setContactForAction(null);
      setSelectedContact(null);
    } catch (error) {
      toast.error("Failed to mark as lost");
    }
  };

  // Move to icebox
  const handleMoveToIcebox = async (wakeDate, notes) => {
    if (!contactForAction) return;
    try {
      await API.post(`/contacts/${contactForAction.contact_id}/icebox`, { wake_date: wakeDate, notes });
      toast.success(`Contact frozen until ${wakeDate}`);
      refreshData();
      setShowIceboxModal(false);
      setContactForAction(null);
      setSelectedContact(null);
    } catch (error) {
      toast.error("Failed to move to icebox");
    }
  };

  // Wake from icebox
  const handleWakeFromIcebox = async (contactId) => {
    try {
      await API.post(`/contacts/${contactId}/wake`);
      toast.success("Contact awakened!");
      refreshData();
    } catch (error) {
      toast.error("Failed to wake contact");
    }
  };

  // Open contact detail
  const openContactDetail = (contact) => {
    setSelectedContact(contact);
    setSearchParams({ contact: contact.contact_id });
  };

  // Close contact detail
  const closeContactDetail = () => {
    setSelectedContact(null);
    searchParams.delete("contact");
    setSearchParams(searchParams);
  };

  // Open lost modal
  const openLostModal = (contact) => {
    setContactForAction(contact);
    setShowLostModal(true);
  };

  // Open icebox modal
  const openIceboxModal = (contact) => {
    setContactForAction(contact);
    setShowIceboxModal(true);
  };

  // Open delete confirmation modal (owner/admin only)
  const openDeleteModal = (contact) => {
    setContactForAction(contact);
    setShowDeleteModal(true);
  };

  // Handle delete contact (owner/admin only)
  const handleDeleteContact = async () => {
    if (!contactForAction || !canDelete) return;
    
    setDeleting(true);
    try {
      console.log('[Delete] Attempting to delete:', contactForAction.contact_id);
      await API.delete(`/contacts/${contactForAction.contact_id}`);
      toast.success("Contact deleted permanently");
      setShowDeleteModal(false);
      setContactForAction(null);
      setSelectedContact(null);
      refreshData();
    } catch (error) {
      console.error('[Delete] Error:', error?.response?.data || error.message);
      toast.error(error?.response?.data?.detail || "Failed to delete contact");
    } finally {
      setDeleting(false);
    }
  };

  // Handle Red Alert view contact
  const handleRedAlertViewContact = async (contactId) => {
    try {
      const response = await API.get(`/contacts/${contactId}`);
      if (response.data?.contact) {
        setSelectedContact(response.data.contact);
        setSearchParams({ contact: contactId });
      }
    } catch (error) {
      console.error("Failed to load contact from red alert:", error);
      // Still refresh and try to find in list
      await loadContacts();
    }
    setRedAlert(null);
  };

  // Calculate active pipeline count
  const activePipelineCount = (counts.new_contact || 0) + (counts.site_visit || 0) + 
                              (counts.estimate_sent || 0) + (counts.follow_up || 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Red Alert Modal */}
      <RedAlertModal
        alert={redAlert}
        onClose={() => setRedAlert(null)}
        onViewContact={handleRedAlertViewContact}
      />
      
      {/* Connection Status (only shows when disconnected) */}
      <ConnectionStatus
        isConnected={isConnected}
        error={connectionError}
        onReconnect={reconnectWs}
      />
      
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">LEAD COMMAND CENTER</h1>
              <p className="text-sm text-slate-500 mt-0.5">Pipeline Management & Contact Intelligence</p>
            </div>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Contact
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Strip */}
      {stats && <StatsStrip stats={stats} />}

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {/* Pipeline Tab */}
            <button
              onClick={() => { setActiveTab("pipeline"); setActiveStage(null); }}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === "pipeline"
                  ? "bg-slate-800 text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/50"
              }`}
            >
              Pipeline
              <Badge className="ml-2 bg-slate-700 text-slate-300">{activePipelineCount}</Badge>
            </button>
            
            {/* Archive Tab */}
            <button
              onClick={() => setActiveTab("archive")}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === "archive"
                  ? "bg-slate-800 text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/50"
              }`}
            >
              Archive
              <Badge className="ml-2 bg-slate-700 text-slate-300">
                {(counts.won || 0) + (counts.lost || 0) + (counts.icebox || 0)}
              </Badge>
            </button>
          </div>

          {/* Search */}
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-700 text-slate-100"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Pipeline View */}
        {activeTab === "pipeline" && (
          <div>
            {/* Stage Filter Pills */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setActiveStage(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activeStage === null
                    ? "bg-slate-700 text-white"
                    : "bg-slate-800/50 text-slate-400 hover:text-white"
                }`}
              >
                All ({activePipelineCount})
              </button>
              {PIPELINE_STAGES.map(stage => (
                <button
                  key={stage.key}
                  onClick={() => setActiveStage(stage.key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    activeStage === stage.key
                      ? `${stage.color} text-white`
                      : "bg-slate-800/50 text-slate-400 hover:text-white"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${stage.color}`}></span>
                  {stage.label} ({counts[stage.key] || 0})
                </button>
              ))}
            </div>

            {/* Contacts Table */}
            <ContactTable
              contacts={contacts}
              loading={loading}
              onContactClick={openContactDetail}
              onStageChange={handleStageChange}
              onMarkLost={openLostModal}
              onMoveToIcebox={openIceboxModal}
              onDelete={openDeleteModal}
              canDelete={canDelete}
              stages={PIPELINE_STAGES}
            />
          </div>
        )}

        {/* Archive View */}
        {activeTab === "archive" && (
          <div>
            {/* Archive Filter Pills */}
            <div className="flex items-center gap-2 mb-4">
              {ARCHIVE_TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setArchiveFilter(tab.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      archiveFilter === tab.key
                        ? `${tab.color} text-white`
                        : "bg-slate-800/50 text-slate-400 hover:text-white"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {tab.label} ({counts[tab.key] || 0})
                  </button>
                );
              })}
            </div>

            {/* Archive Table */}
            <ContactTable
              contacts={contacts}
              loading={loading}
              onContactClick={openContactDetail}
              onWakeFromIcebox={handleWakeFromIcebox}
              onDelete={openDeleteModal}
              canDelete={canDelete}
              isArchive={true}
              archiveType={archiveFilter}
            />
          </div>
        )}
      </div>

      {/* Contact Detail Modal */}
      {selectedContact && (
        <ContactDetail
          contact={selectedContact}
          isOpen={!!selectedContact}
          onClose={closeContactDetail}
          onRefresh={refreshData}
          onStageChange={handleStageChange}
          onMarkWon={handleMarkWon}
          onMarkLost={openLostModal}
          onMoveToIcebox={openIceboxModal}
          onWakeFromIcebox={handleWakeFromIcebox}
          onDelete={openDeleteModal}
          canDelete={canDelete}
          stages={PIPELINE_STAGES}
        />
      )}

      {/* Create Contact Modal */}
      <CreateContactModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(createdContact) => {
          setShowCreateModal(false);
          refreshData();
          // Open scheduler for the newly created contact
          if (createdContact) {
            setNewlyCreatedContact(createdContact);
            setShowScheduler(true);
          }
        }}
      />

      {/* Site Visit Scheduler Modal */}
      <SiteVisitScheduler
        contact={newlyCreatedContact}
        isOpen={showScheduler}
        onClose={() => {
          setShowScheduler(false);
          setNewlyCreatedContact(null);
        }}
        onSuccess={() => {
          setShowScheduler(false);
          setNewlyCreatedContact(null);
          refreshData();
        }}
      />

      {/* Lost Reason Modal */}
      <LostReasonModal
        isOpen={showLostModal}
        contact={contactForAction}
        onClose={() => {
          setShowLostModal(false);
          setContactForAction(null);
        }}
        onSubmit={handleMarkLost}
      />

      {/* Icebox Modal */}
      <IceboxModal
        isOpen={showIceboxModal}
        contact={contactForAction}
        onClose={() => {
          setShowIceboxModal(false);
          setContactForAction(null);
        }}
        onSubmit={handleMoveToIcebox}
      />

      {/* Delete Confirmation Modal (Owner/Admin Only) */}
      {canDelete && (
        <Dialog open={showDeleteModal} onOpenChange={(open) => {
          if (!open) {
            setShowDeleteModal(false);
            setContactForAction(null);
          }
        }}>
          <DialogContent className="max-w-md bg-slate-900 border-red-600/50">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-400">
                <Trash2 className="h-5 w-5" />
                Delete Contact Permanently
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            {contactForAction && (
              <div className="py-4">
                <p className="text-slate-300">
                  Are you sure you want to permanently delete{" "}
                  <span className="font-semibold text-white">
                    {contactForAction.first_name} {contactForAction.last_name}
                  </span>
                  ?
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  This will also delete all associated communications, activity history, and documents.
                </p>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setContactForAction(null);
                }}
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteContact}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-500"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {deleting ? "Deleting..." : "Delete Permanently"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default LeadCommandCenter;
